class RocketSection extends HTMLElement {
  static isIOS() {
    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
  }

  constructor() {
    super();

    const dir = this.dataset.direction;
    this.direction = dir === 'right' ? 'right' : 'left';

    const duration = this.dataset.animationDuration;
    const parsed = parseInt(duration || '', 10);
    this.duration = Number.isFinite(parsed) ? parsed : 2000;

    this.rocket = this.querySelector('.rocket-svg');
    this.trails = this.querySelectorAll('.trail');
    this.moveAnimation = null;
    this.observer = null;
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.moveAnimation) {
      this.moveAnimation.cancel();
    }
  }

  init() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.activate();
            this.observer.disconnect();
          }
        });
      },
      { threshold: 0.25, rootMargin: '0px 0px -5% 0px' }
    );

    this.observer.observe(this);
  }

  setDimensions() {
    const track = this.querySelector('.rocket-track');
    if (!track) return;

    const trackWidth = track.offsetWidth;
    const travel = trackWidth * 0.8;

    this.style.setProperty('--rocket-travel', `${travel}px`);
    this.style.setProperty('--trail-middle-width', `${Math.max(0, travel - 100)}px`);
    this.style.setProperty('--trail-top-width', `${Math.max(0, trackWidth * 0.5 - 88)}px`);
    this.style.setProperty('--trail-bottom-width', `${Math.max(0, trackWidth * 0.65 - 88)}px`);
    this.style.setProperty('--trail-container-width', `${travel}px`);

    return travel;
  }

  animateWithWAAPI(travel) {
    if (!this.rocket) return;

    const isRight = this.direction === 'right';
    const startX = isRight ? 100 : -100;
    const endX = isRight ? -travel : travel;

    this.rocket.getAnimations().forEach((animation) => animation.cancel());

    this.moveAnimation = this.rocket.animate(
      [
        { transform: `translateX(${startX}px)` },
        { transform: `translateX(${endX}px)` },
      ],
      {
        duration: this.duration,
        easing: 'ease-out',
        fill: 'forwards',
      }
    );

    this.moveAnimation.addEventListener(
      'finish',
      () => {
        this.rocket.style.transform = `translateX(${endX}px)`;
      },
      { once: true }
    );
  }

  activate() {
    const start = () => {
      const travel = this.setDimensions();
      if (!travel || !this.rocket) return;

      if (RocketSection.isIOS()) {
        this.classList.add('uses-waapi');
        this.animateWithWAAPI(travel);
      } else {
        this.rocket.addEventListener(
          'animationend',
          () => {
            this.rocket.style.willChange = 'auto';
          },
          { once: true }
        );
      }

      this.classList.add('is-active');

      if (this.getAttribute('data-shake') === 'true') {
        setTimeout(() => {
          this.classList.add('shake-ended');
        }, this.duration / 3);
      }
    };

    if (RocketSection.isIOS()) {
      requestAnimationFrame(() => requestAnimationFrame(start));
    } else {
      requestAnimationFrame(start);
    }
  }
}

if (!customElements.get('rocket-section')) {
  customElements.define('rocket-section', RocketSection);
}
